<script lang="ts">
  import { untrack } from 'svelte';
  import { api } from '../../api/client';
  import { TRIP_KINDS, type AlertSetting, type Place, type TripKind } from '../../api/types';
  import { t } from '../../i18n/index.ts';

  // AlertEditor edits one alert setting: trip ends and the arrival and
  // departure rules, saved as a whole. subject is a member's ID or
  // 'everyone'.
  let { groupId, subject, setting, places, onSaved, onCancel }: {
    groupId: string;
    subject: string;
    setting: AlertSetting;
    places: Place[];
    onSaved: () => void;
    onCancel: () => void;
  } = $props();

  interface Row {
    place: string; // a place ID, or '' for every place
    arrive: boolean;
    depart: boolean;
  }

  // The editor starts from the setting as it was opened and is remade for
  // each one, so later changes to the prop are not followed.
  let tripEnds: TripKind[] = $state(untrack(() => [...setting.trip_ends]));
  let phone = $state(untrack(() => !!setting.phone));
  let rows: Row[] = $state(untrack(() => setting.places.map((r) => ({ place: r.place_id ?? '', arrive: r.on_arrive, depart: r.on_depart }))));
  let error = $state('');
  let busy = $state(false);

  function add() {
    const used = new Set(rows.map((r) => r.place));
    const free = ['', ...places.map((p) => p.id)].find((id) => !used.has(id));
    if (free === undefined) {
      error = t('alertsEveryPlaceHasRule');
      return;
    }
    rows = [...rows, { place: free, arrive: true, depart: true }];
  }

  async function save() {
    error = '';
    if (rows.some((r) => !r.arrive && !r.depart)) {
      error = t('alertsNeedArriveOrLeave');
      return;
    }
    if (new Set(rows.map((r) => r.place)).size !== rows.length) {
      error = t('alertsPlaceOnce');
      return;
    }
    busy = true;
    try {
      await api.put(`/groups/${groupId}/alerts/${subject}`, {
        trip_ends: tripEnds,
        phone,
        places: rows.map((r) => ({ place_id: r.place || undefined, on_arrive: r.arrive, on_depart: r.depart })),
      });
      onSaved();
    } catch (e: any) {
      error = e.message;
    } finally {
      busy = false;
    }
  }
</script>

<div class="space-y-3 text-sm">
  <fieldset>
    <legend class="font-semibold">{t('alertsTripEnds')}</legend>
    <div class="flex flex-wrap gap-x-4 gap-y-1 mt-1">
      {#each TRIP_KINDS as kind}
        <label class="flex items-center gap-2">
          <input type="checkbox" bind:group={tripEnds} value={kind} />
          <span>{t('alertsTripKind', { kind })}</span>
        </label>
      {/each}
    </div>
  </fieldset>
  <label class="flex items-start gap-2">
    <input type="checkbox" class="mt-1" bind:checked={phone} />
    <span>
      <span class="font-semibold">{t('alertsPhone')}</span>
      <span class="block text-text-muted">{t('alertsPhoneDetail')}</span>
    </span>
  </label>
  <div class="space-y-2">
    <p class="font-semibold">{t('alertsArrivalsDepartures')}</p>
    {#each rows as row, i (i)}
      <div class="flex flex-wrap items-center gap-2">
        <select class="input py-1" bind:value={row.place} aria-label={t('alertsPlace')}>
          <option value="">{t('alertsEveryPlace')}</option>
          {#each places as p (p.id)}
            <option value={p.id}>{p.name}</option>
          {/each}
        </select>
        <label class="flex items-center gap-1"><input type="checkbox" bind:checked={row.arrive} />{t('alertsArrives')}</label>
        <label class="flex items-center gap-1"><input type="checkbox" bind:checked={row.depart} />{t('alertsLeaves')}</label>
        <button class="text-text-muted hover:opacity-80" title={t('alertsRemoveRule')} aria-label={t('alertsRemoveRule')} onclick={() => (rows = rows.filter((_, k) => k !== i))}>
          <i aria-hidden="true" class="las la-times"></i>
        </button>
      </div>
    {:else}
      <p class="text-text-muted">{t('alertsNoRules')}</p>
    {/each}
    <button class="btn btn-secondary btn-sm" onclick={add}><i aria-hidden="true" class="las la-plus mr-1"></i>{t('alertsAddPlace')}</button>
  </div>
  {#if error}
    <p style="color: var(--theme-error);">{error}</p>
  {/if}
  <div class="flex gap-2">
    <button class="btn btn-primary btn-sm" disabled={busy} onclick={save}>{t('appSave')}</button>
    <button class="btn btn-secondary btn-sm" onclick={onCancel}>{t('appCancel')}</button>
  </div>
</div>
